/**
 * TaskWidget - Form laterale completo per task
 *
 * Features:
 * - Header con timer, azioni rapide
 * - Alert per task scadute
 * - Tabs: Dettagli / Sottoattività
 * - Campi: stato, priorità, assegnatari, date, tempo stimato, tag
 * - Sottoattività con checkbox
 * - Registri di tempo
 * - File drag & drop
 * - Commenti e attività
 */

import { useState, useMemo, useEffect } from 'react';
import {
  X,
  Clock,
  Link2,
  Bell,
  RefreshCw,
  Eye,
  Mail,
  ExternalLink,
  MoreHorizontal,
  Play,
  Square,
  Plus,
  ChevronDown,
  AlertTriangle,
  CheckSquare,
  Calendar,
  Users,
  Tag,
  MessageSquare,
  Activity,
  Upload,
  Check,
  Trash2,
} from 'lucide-react';
import { useProjectsStore } from '@business-assistant/shared';
import { useTeamMembers, useActiveTimer, useStartTimer, useStopTimer } from '@business-assistant/shared';
import type { Task, TaskStatus, TaskPriority } from '@business-assistant/shared/domain';
import { DetailPanel, DetailSection } from '../common/base';
import { useTranslation } from '../../hooks/useTranslation';
import { Button, TextAreaField, SelectField, formStyles as sharedFormStyles } from '../common/forms';

// =============================================================================
// TYPES
// =============================================================================

interface TaskWidgetProps {
  projectId: string;
  taskId?: string;
  onClose: () => void;
  onSave: (data: TaskFormData) => void;
}

export interface TaskFormData {
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  assignees: string[];
  startAt?: string;
  dueAt?: string;
  estimatedHours?: number;
  tags: string[];
  parentId?: string;
  checklist: { id: string; text: string; done: boolean }[];
}

// =============================================================================
// CONSTANTS
// =============================================================================

const STATUS_OPTIONS: { value: TaskStatus; labelKey: string; color: string }[] = [
  { value: 'backlog', labelKey: 'projects.taskStatus.backlog', color: '#6b7280' },
  { value: 'todo', labelKey: 'projects.taskStatus.todo', color: '#3b82f6' },
  { value: 'in-progress', labelKey: 'projects.taskStatus.doing', color: '#f59e0b' },
  { value: 'review', labelKey: 'projects.taskStatus.review', color: '#8b5cf6' },
  { value: 'done', labelKey: 'projects.taskStatus.done', color: '#22c55e' },
];

const PRIORITY_OPTIONS: { value: TaskPriority; labelKey: string; color: string }[] = [
  { value: 'none', labelKey: 'projects.priority.none', color: '#9ca3af' },
  { value: 'low', labelKey: 'projects.priority.low', color: '#22c55e' },
  { value: 'medium', labelKey: 'projects.priority.medium', color: '#f59e0b' },
  { value: 'high', labelKey: 'projects.priority.high', color: '#ef4444' },
  { value: 'urgent', labelKey: 'projects.priority.urgent', color: '#dc2626' },
];

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  headerToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    borderBottom: '1px solid var(--line)',
    background: '#fafafa',
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    padding: '0.5rem',
    cursor: 'pointer',
    borderRadius: '0.375rem',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  timeDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.875rem',
    color: '#666',
    padding: '0.25rem 0.5rem',
    background: '#f3f4f6',
    borderRadius: '0.25rem',
  },
  alertBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    background: '#fef2f2',
    borderBottom: '1px solid #fecaca',
    color: '#dc2626',
    fontSize: '0.875rem',
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid var(--line)',
    padding: '0 1rem',
    background: 'white',
  },
  tab: {
    padding: '0.75rem 1rem',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#666',
  },
  tabActive: {
    borderBottomColor: 'var(--brand)',
    color: 'var(--brand)',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    padding: '0.625rem 0',
    borderBottom: '1px solid var(--line)',
  },
  fieldLabel: {
    width: '120px',
    flexShrink: 0,
    fontSize: '0.875rem',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  fieldValue: {
    flex: 1,
    fontSize: '0.875rem',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  subtaskItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.625rem 0.75rem',
    borderBottom: '1px solid var(--line)',
    cursor: 'pointer',
  },
  subtaskAdd: {
    padding: '0.75rem',
    background: '#f9fafb',
  },
  timelogSection: {
    background: '#f9fafb',
    borderRadius: '0.375rem',
    padding: '0.75rem',
  },
  timelogStat: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.375rem 0',
    fontSize: '0.875rem',
  },
  filesDropzone: {
    border: '2px dashed #d1d5db',
    borderRadius: '0.5rem',
    padding: '2rem',
    textAlign: 'center' as const,
    color: '#9ca3af',
    cursor: 'pointer',
  },
  commentsEmpty: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#9ca3af',
  },
  commentInput: {
    display: 'flex',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    borderTop: '1px solid var(--line)',
    background: 'white',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'var(--brand)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 600,
    flexShrink: 0,
  },
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
  },
  select: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    background: 'white',
  },
};

// =============================================================================
// COMPONENT
// =============================================================================

export function TaskWidget({ projectId, taskId, onClose, onSave }: TaskWidgetProps) {
  const { t } = useTranslation();
  const { getTask, getProject } = useProjectsStore();
  const { data: teamMembers = [] } = useTeamMembers();
  const { data: activeTimer } = useActiveTimer();
  const startTimerMutation = useStartTimer();
  const stopTimerMutation = useStopTimer();

  const startTimer = (taskId: string, projectId: string) => {
    startTimerMutation.mutate({ taskId, projectId });
  };

  const stopTimer = () => {
    stopTimerMutation.mutate();
  };

  const existingTask = taskId ? getTask(taskId) : null;
  const project = getProject(projectId);
  const isTimerActive = activeTimer?.taskId === taskId;

  // Form state
  const [formData, setFormData] = useState<TaskFormData>({
    title: existingTask?.title || '',
    description: existingTask?.description || '',
    status: existingTask?.status || 'todo',
    priority: existingTask?.priority || 'none',
    assignees: existingTask?.assignees || [],
    startAt: existingTask?.startAt,
    dueAt: existingTask?.dueAt,
    estimatedHours: (existingTask as any)?.estimatedHours,
    tags: existingTask?.tags || [],
    parentId: existingTask?.parentId,
    checklist: existingTask?.checklist || [],
  });

  const [activeTab, setActiveTab] = useState<'details' | 'subtasks'>('details');
  const [commentsTab, setCommentsTab] = useState<'comments' | 'activity'>('comments');
  const [newSubtask, setNewSubtask] = useState('');
  const [showCompletedSubtasks, setShowCompletedSubtasks] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [comment, setComment] = useState('');

  // Timer elapsed
  const [elapsed, setElapsed] = useState('00:00');

  useEffect(() => {
    if (!isTimerActive || !activeTimer) return;
    const interval = setInterval(() => {
      const start = new Date(activeTimer.startedAt).getTime();
      const now = Date.now();
      const totalSeconds = Math.floor((now - start) / 1000);
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      setElapsed(`${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`);
    }, 1000);
    return () => clearInterval(interval);
  }, [isTimerActive, activeTimer]);

  // Computed
  const isOverdue = useMemo(() => {
    if (!formData.dueAt || formData.status === 'done') return false;
    return new Date(formData.dueAt) < new Date();
  }, [formData.dueAt, formData.status]);

  const daysOverdue = useMemo(() => {
    if (!isOverdue || !formData.dueAt) return 0;
    const diff = Date.now() - new Date(formData.dueAt).getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }, [isOverdue, formData.dueAt]);

  const subtasks = formData.checklist || [];
  const completedSubtasks = subtasks.filter(s => s.done);
  const pendingSubtasks = subtasks.filter(s => !s.done);

  // Demo time logs
  const totalLogged = 9.43;
  const billable = 9.43;
  const nonBillable = 0;

  // Handlers
  const handleSubmit = () => {
    if (!formData.title.trim()) return;
    onSave(formData);
  };

  const toggleSubtask = (id: string) => {
    setFormData({
      ...formData,
      checklist: formData.checklist.map(s =>
        s.id === id ? { ...s, done: !s.done } : s
      ),
    });
  };

  const addSubtask = () => {
    if (!newSubtask.trim()) return;
    setFormData({
      ...formData,
      checklist: [
        ...formData.checklist,
        { id: `sub-${Date.now()}`, text: newSubtask.trim(), done: false },
      ],
    });
    setNewSubtask('');
  };

  const removeSubtask = (id: string) => {
    setFormData({
      ...formData,
      checklist: formData.checklist.filter(s => s.id !== id),
    });
  };

  const addTag = () => {
    if (!tagInput.trim() || formData.tags.includes(tagInput.trim())) return;
    setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
    setTagInput('');
  };

  const toggleAssignee = (memberId: string) => {
    if (formData.assignees.includes(memberId)) {
      setFormData({ ...formData, assignees: formData.assignees.filter(id => id !== memberId) });
    } else {
      setFormData({ ...formData, assignees: [...formData.assignees, memberId] });
    }
  };

  const statusConfig = STATUS_OPTIONS.find(s => s.value === formData.status);
  const priorityConfig = PRIORITY_OPTIONS.find(p => p.value === formData.priority);

  return (
    <DetailPanel
      isOpen={true}
      onClose={onClose}
      title=""
      headerColor="#8b5cf6"
      width="50%"
    >
      {/* Custom Header */}
      <div style={styles.headerToolbar}>
        <div style={styles.headerButtons}>
          <button
            style={{
              ...styles.iconBtn,
              background: isTimerActive ? '#dcfce7' : 'transparent',
              color: isTimerActive ? '#16a34a' : '#666',
            }}
            onClick={() => isTimerActive ? stopTimer() : startTimer(taskId || '', projectId)}
            title={isTimerActive ? t('tasks.stopTimer') : t('tasks.startTimer')}
          >
            {isTimerActive ? <Square size={16} /> : <Play size={16} />}
          </button>
          <div style={styles.timeDisplay}>
            <Clock size={14} />
            {isTimerActive ? elapsed : `${Math.floor(totalLogged)}h ${Math.round((totalLogged % 1) * 60)}m`}
          </div>
          <span style={{ color: '#ccc' }}>/</span>
          <div style={styles.timeDisplay}>
            {formData.estimatedHours ? `${formData.estimatedHours}h` : '0h'}
          </div>
        </div>
        <div style={styles.headerButtons}>
          <button style={styles.iconBtn} title={t('tasks.copyLink')}><Link2 size={16} /></button>
          <button style={styles.iconBtn} title={t('tasks.reminder')}><Bell size={16} /></button>
          <button style={styles.iconBtn} title={t('tasks.recurring')}><RefreshCw size={16} /></button>
          <button style={styles.iconBtn} title={t('tasks.follow')}><Eye size={16} /></button>
          <button style={styles.iconBtn} title="Email"><Mail size={16} /></button>
          <button style={styles.iconBtn} title={t('tasks.openProject')}><ExternalLink size={16} /></button>
          <button style={styles.iconBtn} title={t('tasks.other')}><MoreHorizontal size={16} /></button>
          <button style={styles.iconBtn} onClick={onClose} title={t('common.close')}><X size={16} /></button>
        </div>
      </div>

      {/* Alert Banner */}
      {isOverdue && (
        <div style={styles.alertBanner}>
          <AlertTriangle size={18} />
          <span>
            {t(daysOverdue === 1 ? 'tasks.overdueBy' : 'tasks.overdueBy_plural', { count: daysOverdue })}
          </span>
        </div>
      )}

      {/* Title Input */}
      <div style={{ padding: '1rem' }}>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder={t('tasks.taskName')}
          style={{
            ...styles.input,
            fontSize: '1.125rem',
            fontWeight: 600,
            border: 'none',
            padding: 0,
          }}
          autoFocus={!taskId}
        />
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'details' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('details')}
        >
          {t('tasks.details')}
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'subtasks' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('subtasks')}
        >
          {t('tasks.subtasks')}
          {subtasks.length > 0 && (
            <span style={{
              marginLeft: '0.5rem',
              background: '#e5e7eb',
              padding: '0.125rem 0.5rem',
              borderRadius: '9999px',
              fontSize: '0.75rem',
            }}>
              {subtasks.length}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: '1rem', overflowY: 'auto', flex: 1 }}>
        {activeTab === 'details' && (
          <>
            {/* Fields */}
            <div style={{ marginBottom: '1.5rem' }}>
              {/* Status */}
              <div style={styles.fieldRow}>
                <div style={styles.fieldLabel}>
                  <CheckSquare size={14} /> {t('tasks.status')}
                </div>
                <div style={styles.fieldValue}>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as TaskStatus })}
                    style={{
                      ...styles.select,
                      color: statusConfig?.color,
                      fontWeight: 500,
                    }}
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Priority */}
              <div style={styles.fieldRow}>
                <div style={styles.fieldLabel}>
                  <AlertTriangle size={14} /> {t('tasks.priority')}
                </div>
                <div style={styles.fieldValue}>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as TaskPriority })}
                    style={{
                      ...styles.select,
                      color: priorityConfig?.color,
                      fontWeight: 500,
                    }}
                  >
                    {PRIORITY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Assignees */}
              <div style={styles.fieldRow}>
                <div style={styles.fieldLabel}>
                  <Users size={14} /> {t('tasks.assignees')}
                </div>
                <div style={styles.fieldValue}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                    {teamMembers.filter(m => m.status === 'attivo').slice(0, 8).map(member => {
                      const isSelected = formData.assignees.includes(member.id);
                      return (
                        <button
                          key={member.id}
                          type="button"
                          onClick={() => toggleAssignee(member.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.375rem',
                            padding: '0.25rem 0.5rem',
                            border: isSelected ? '2px solid var(--brand)' : '1px solid var(--line)',
                            borderRadius: '9999px',
                            background: isSelected ? 'rgba(139, 92, 246, 0.1)' : 'white',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                          }}
                        >
                          <div style={{
                            ...styles.avatar,
                            width: '20px',
                            height: '20px',
                            fontSize: '0.625rem',
                          }}>
                            {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          {member.name.split(' ')[0]}
                          {isSelected && <Check size={12} style={{ color: 'var(--brand)' }} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Start Date */}
              <div style={styles.fieldRow}>
                <div style={styles.fieldLabel}>
                  <Calendar size={14} /> {t('tasks.startDate')}
                </div>
                <div style={styles.fieldValue}>
                  <input
                    type="date"
                    value={formData.startAt || ''}
                    onChange={(e) => setFormData({ ...formData, startAt: e.target.value || undefined })}
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Due Date */}
              <div style={styles.fieldRow}>
                <div style={styles.fieldLabel}>
                  <Calendar size={14} /> {t('tasks.dueDate')}
                </div>
                <div style={styles.fieldValue}>
                  <input
                    type="date"
                    value={formData.dueAt || ''}
                    onChange={(e) => setFormData({ ...formData, dueAt: e.target.value || undefined })}
                    style={{
                      ...styles.input,
                      ...(isOverdue ? { borderColor: '#dc2626', color: '#dc2626' } : {}),
                    }}
                  />
                </div>
              </div>

              {/* Estimated Time */}
              <div style={styles.fieldRow}>
                <div style={styles.fieldLabel}>
                  <Clock size={14} /> {t('tasks.estimatedTime')}
                </div>
                <div style={styles.fieldValue}>
                  <input
                    type="number"
                    value={formData.estimatedHours || ''}
                    onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value ? parseFloat(e.target.value) : undefined })}
                    min="0"
                    step="0.5"
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Tags */}
              <div style={styles.fieldRow}>
                <div style={styles.fieldLabel}>
                  <Tag size={14} /> {t('tasks.tags')}
                </div>
                <div style={styles.fieldValue}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.5rem' }}>
                    {formData.tags.map(tag => (
                      <span key={tag} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.125rem 0.5rem',
                        background: '#e5e7eb',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                      }}>
                        {tag}
                        <button
                          onClick={() => setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) })}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#666' }}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      placeholder={t('tasks.addTag')}
                      style={{ ...styles.input, flex: 1 }}
                    />
                    <button onClick={addTag} style={{ ...styles.iconBtn, border: '1px solid var(--line)' }}>
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Task ID */}
              {taskId && (
                <div style={styles.fieldRow}>
                  <div style={styles.fieldLabel}>
                    <Link2 size={14} /> {t('tasks.taskId')}
                  </div>
                  <div style={styles.fieldValue}>
                    <span style={{ color: 'var(--brand)', cursor: 'pointer' }}>#{taskId}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            <DetailSection title={t('tasks.description')}>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('tasks.addDescription')}
                rows={4}
                style={{ ...styles.input, resize: 'vertical' }}
              />
            </DetailSection>

            {/* Time Logs */}
            <DetailSection title={`${t('tasks.timeLogs')} (${Math.floor(totalLogged)}h ${Math.round((totalLogged % 1) * 60)}m)`}>
              <div style={styles.timelogSection}>
                <div style={styles.timelogStat}>
                  <span style={{ color: '#666' }}>{t('tasks.totalLogged')}</span>
                  <span style={{ fontWeight: 500 }}>{totalLogged.toFixed(2)}h</span>
                </div>
                <div style={styles.timelogStat}>
                  <span style={{ color: '#666' }}>{t('tasks.billable')}</span>
                  <span style={{ fontWeight: 500, color: '#16a34a' }}>{billable.toFixed(2)}h</span>
                </div>
                <div style={styles.timelogStat}>
                  <span style={{ color: '#666' }}>{t('tasks.nonBillable')}</span>
                  <span style={{ fontWeight: 500 }}>{nonBillable.toFixed(2)}h</span>
                </div>
              </div>
              <button style={{
                width: '100%',
                marginTop: '0.75rem',
                padding: '0.5rem',
                background: 'white',
                border: '1px solid var(--line)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
              }}>
                <Plus size={16} /> {t('tasks.logTime')}
              </button>
            </DetailSection>

            {/* Files */}
            <DetailSection title={t('tasks.files')}>
              <div style={styles.filesDropzone}>
                <Upload size={32} style={{ marginBottom: '0.5rem' }} />
                <div>{t('tasks.dragDropFiles')}</div>
                <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{t('tasks.orClickToBrowse')}</div>
              </div>
            </DetailSection>
          </>
        )}

        {activeTab === 'subtasks' && (
          <>
            {/* Pending subtasks */}
            <div style={{ border: '1px solid var(--line)', borderRadius: '0.375rem', marginBottom: '1rem' }}>
              {pendingSubtasks.map(subtask => (
                <div
                  key={subtask.id}
                  style={styles.subtaskItem}
                  onClick={() => toggleSubtask(subtask.id)}
                >
                  <input
                    type="checkbox"
                    checked={subtask.done}
                    onChange={() => toggleSubtask(subtask.id)}
                    style={{ width: '18px', height: '18px', accentColor: 'var(--brand)' }}
                  />
                  <span style={{ flex: 1 }}>{subtask.text}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeSubtask(subtask.id); }}
                    style={{ ...styles.iconBtn, color: '#ef4444' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}

              {/* Add subtask */}
              <div style={styles.subtaskAdd}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={newSubtask}
                    onChange={(e) => setNewSubtask(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSubtask())}
                    placeholder={t('tasks.addSubtask')}
                    style={{ ...styles.input, flex: 1 }}
                  />
                  <button onClick={addSubtask} style={{
                    padding: '0.5rem 1rem',
                    background: 'var(--brand)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                  }}>
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Completed subtasks */}
            {completedSubtasks.length > 0 && (
              <>
                <button
                  onClick={() => setShowCompletedSubtasks(!showCompletedSubtasks)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: '#f9fafb',
                    border: '1px solid var(--line)',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    color: '#666',
                  }}
                >
                  <ChevronDown size={16} style={{ transform: showCompletedSubtasks ? 'rotate(180deg)' : 'none' }} />
                  {showCompletedSubtasks ? t('tasks.hideCompleted') : t('tasks.showCompleted')} {completedSubtasks.length} {t('tasks.completedTasks')}
                </button>

                {showCompletedSubtasks && (
                  <div style={{ border: '1px solid var(--line)', borderRadius: '0.375rem', marginTop: '0.5rem' }}>
                    {completedSubtasks.map(subtask => (
                      <div
                        key={subtask.id}
                        style={{ ...styles.subtaskItem, opacity: 0.6 }}
                        onClick={() => toggleSubtask(subtask.id)}
                      >
                        <input
                          type="checkbox"
                          checked={subtask.done}
                          onChange={() => toggleSubtask(subtask.id)}
                          style={{ width: '18px', height: '18px', accentColor: 'var(--brand)' }}
                        />
                        <span style={{ flex: 1, textDecoration: 'line-through' }}>{subtask.text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Comments/Activity Tabs */}
        <div style={{ ...styles.tabs, marginTop: '1.5rem', padding: 0 }}>
          <button
            style={{ ...styles.tab, ...(commentsTab === 'comments' ? styles.tabActive : {}) }}
            onClick={() => setCommentsTab('comments')}
          >
            <MessageSquare size={14} style={{ marginRight: '0.5rem' }} />
            {t('tasks.comments')}
          </button>
          <button
            style={{ ...styles.tab, ...(commentsTab === 'activity' ? styles.tabActive : {}) }}
            onClick={() => setCommentsTab('activity')}
          >
            <Activity size={14} style={{ marginRight: '0.5rem' }} />
            {t('tasks.activity')}
          </button>
        </div>

        {/* Comments Empty State */}
        <div style={styles.commentsEmpty}>
          <MessageSquare size={48} style={{ marginBottom: '0.75rem', opacity: 0.5 }} />
          <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>{t('tasks.noComments')}</div>
          <div style={{ fontSize: '0.875rem' }}>
            {t('tasks.mentionToStart')}
          </div>
        </div>
      </div>

      {/* Comment Input */}
      <div style={styles.commentInput}>
        <div style={styles.avatar}>PM</div>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('tasks.addComment')}
          style={{ ...styles.input, flex: 1, resize: 'none', minHeight: '40px' }}
        />
      </div>

      {/* Save Button */}
      <div style={{
        padding: '0.75rem 1rem',
        borderTop: '1px solid var(--line)',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
        background: 'white',
      }}>
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!formData.title.trim()}
        >
          {taskId ? t('tasks.saveTask') : t('tasks.createTask')}
        </Button>
      </div>
    </DetailPanel>
  );
}
